import { useTicker } from '@monogrid/vue-boost/core'
import { DOMToPlane, useDOMToPlane } from '@monogrid/vue-boost/three'
import gsap from 'gsap'
import { BufferGeometry, Mesh, ShaderMaterial } from 'three'
import { ComponentPublicInstance, computed, inject, MaybeRefOrGetter, toValue, watch } from 'vue'

import { lenisInjectionKey } from '@/App.vue'
import { getMaterialBasicAsset, getMaterialDitheredAsset } from '@/three/utils/materialLib.js'
import { threeEnvironmentInjection } from '@/three/WebGLApp.vue'

type UseMediaMeshConfig = {
  autoReveal?: MaybeRefOrGetter<boolean>
  cover?: MaybeRefOrGetter<boolean>
  contain?: MaybeRefOrGetter<boolean>
  dithered?: MaybeRefOrGetter<boolean>
  enabled?: MaybeRefOrGetter<boolean>
  hover?: MaybeRefOrGetter<number>
  opacity?: MaybeRefOrGetter<number>
  order?: MaybeRefOrGetter<number>
  transparent?: MaybeRefOrGetter<boolean>
  scale?: MaybeRefOrGetter<number>
  squareSize?: MaybeRefOrGetter<number>
  revealMix?: MaybeRefOrGetter<number>
  waveStrength?: MaybeRefOrGetter<number>
  z?: MaybeRefOrGetter<number>
  mediaSize: MaybeRefOrGetter<[number, number]>
}

export function useMediaMesh (el: MaybeRefOrGetter<HTMLElement | ComponentPublicInstance>, config: UseMediaMeshConfig) {
  const environment = inject(threeEnvironmentInjection)
  if (!environment) throw new Error('[useMediaMesh] A ThreeEnvironment is not provided')
  const lenis = inject(lenisInjectionKey)

  const autoReveal = computed(() => toValue(config.autoReveal) !== false)
  const enabled = computed(() => toValue(config.enabled) !== false)
  const subdivisions = computed(() => toValue(config.dithered) ? 1 : 10)

  const material = computed(() => toValue(config.dithered)
    ? getMaterialDitheredAsset({ cover: toValue(config.cover), contain: toValue(config.contain), transparent: toValue(config.transparent) })
    : getMaterialBasicAsset({ cover: toValue(config.cover), contain: toValue(config.contain), transparent: toValue(config.transparent) })
  )

  const { mesh, domMapping } = useDOMToPlane(el, {
    material,
    subdivisions,
    order: config.order,
    domMappingOptions: {
      boundingOptions: {
        intersectionOptions: {
          rootMargin: '40px 0px'
        },
        listenScroll: false
      },
      z: config.z
    }
  }) as {mesh: Mesh<BufferGeometry, ShaderMaterial>, domMapping: DOMToPlane['domMapping']}

  // const meshID = computed(() => mesh.uuid)
  const isWebGLActive = computed(() => enabled.value && domMapping.visible.value)

  function animateReveal () {
    if (!mesh.material.uniforms.uReveal) return
    if (!autoReveal.value) {
      mesh.material.uniforms.uReveal.value = 1
    } else {
      gsap.to(mesh.material.uniforms.uReveal, {
        value: 1,
        duration: 0.65,
        ease: 'sine.inOut',
        delay: 0.5
      })
    }
  }

  const applyUniform = (name, value) => {
    if (mesh.material.uniforms[name]) mesh.material.uniforms[name].value = value
  }

  function onTick ({ time }) {
    applyUniform('uTime', time)
    applyUniform('tTrail', environment.globals.cursorTrail.value.getTarget() || null)
  }
  const { toggle } = useTicker(onTick)

  watch(isWebGLActive, visible => {
    if (visible) animateReveal()
    toggle(visible)
    mesh.visible = visible
  }, { immediate: true })

  watch(() => toValue(config.waveStrength), value => {
    applyUniform('uWaveStrength', value === undefined ? 1 : value)
  }, { immediate: true })

  watch(() => toValue(config.revealMix), value => {
    applyUniform('uRevealMix', value === undefined ? 0 : value)
  }, { immediate: true })

  watch(() => toValue(config.hover), value => {
    applyUniform('uHover', value === undefined ? 0 : value)
  }, { immediate: true })

  watch(() => toValue(config.scale), value => {
    applyUniform('uScale', value === undefined ? 1 : value)
  }, { immediate: true })

  watch(() => toValue(config.opacity), value => {
    applyUniform('uAlpha', value === undefined ? 1 : value)
  }, { immediate: true })

  watch(() => toValue(config.squareSize), value => {
    applyUniform('uSquareSize', value === undefined ? 3 : value)
  }, { immediate: true })

  watch(() => lenis.instance.value, _lenis => {
    _lenis?.on('scroll', () => domMapping.update())
  }, { immediate: true })

  watch([domMapping.width, domMapping.height], ([w, h]) => {
    mesh.material.uniforms.uPlaneSize.value.set(w, h)
  }, { immediate: true })

  watch(() => toValue(config.mediaSize), (_size) => {
    mesh.material.uniforms.uImgSize.value.set(_size[0], _size[1])
  }, { immediate: true })

  watch([
    () => toValue(environment.width),
    () => toValue(environment.height),
    () => environment.webgl.renderer
  ], ([w, h, renderer]) => {
    if (!renderer) return
    const dpr = renderer.getPixelRatio() || 1
    mesh.material.uniforms.uResolution.value.set(w * dpr, h * dpr)
  }, { immediate: true })

  return {
    mesh,
    domMapping
  }
}
